import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { CalendarDays, Clock, MapPin, User, Users, Check, Repeat, CalendarPlus, X, Sparkles } from "lucide-react";
import { pget, ppost, pdel } from "./portalApi";
import { CATEGORY_COLORS } from "@/components/EventsCalendar";

const fmtDate = (iso) => { try { return new Date(iso + "T00:00:00").toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" }); } catch { return iso; } };

export default function PortalEvents() {
  const [data, setData] = useState(null);
  const [scope, setScope] = useState("upcoming");
  const [category, setCategory] = useState("");
  const [party, setParty] = useState({});
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    const qs = new URLSearchParams({ scope, ...(category ? { category } : {}) }).toString();
    pget(`/resident/events?${qs}`).then((r) => setData(r.data)).catch(() => setData({ items: [], rsvp_enabled: false, categories: [] }));
  }, [scope, category]);
  useEffect(() => { load(); }, [load]);

  const rsvp = async (e) => {
    setBusy(e.id);
    try {
      const r = await ppost(`/resident/events/${e.id}/rsvp`, { party_size: Number(party[e.id] || e.my_rsvp || 1) });
      toast.success(r.data.waitlisted ? "That one's full — you're on the waitlist and we'll text you if a spot opens." : r.data.updated ? "RSVP updated" : "You're on the list — see you there!");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't RSVP right now"); }
    finally { setBusy(""); }
  };
  const cancel = async (e) => {
    if (!window.confirm(`Take your household off the list for “${e.title}”?`)) return;
    setBusy(e.id);
    try { await pdel(`/resident/events/${e.id}/rsvp`); toast.success("You're off the list."); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Couldn't cancel"); }
    finally { setBusy(""); }
  };

  const items = data?.items || [];
  return (
    <div className="space-y-6" data-testid="portal-events-page">
      <div>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight flex items-center gap-2"><CalendarDays className="h-7 w-7 text-redline-500" strokeWidth={1.75} /> Events</h1>
        <p className="text-muted-foreground mt-1">What&apos;s on around the community{data?.rsvp_enabled ? " — RSVP right here, or by text or phone." : "."}</p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {[["upcoming", "Coming up"], ["past", "Past"]].map(([v, l]) => (
          <button key={v} data-testid={`portal-events-tab-${v}`} onClick={() => setScope(v)} aria-pressed={scope === v}
            className={`rounded-full px-3.5 py-1.5 text-sm font-semibold ${scope === v ? "bg-ink text-white" : "bg-white border border-sand-200 text-ink/70"}`}>{l}</button>
        ))}
        {(data?.categories || []).length > 0 && (
          <select data-testid="portal-events-category" value={category} onChange={(e) => setCategory(e.target.value)} className="ml-auto rounded-full border border-sand-200 bg-white px-3 py-1.5 text-sm outline-none">
            <option value="">All themes</option>
            {data.categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
        )}
      </div>
      {data === null ? <div className="flex justify-center py-16"><div className="h-7 w-7 rounded-full border-2 border-redline-500 border-t-transparent animate-spin" /></div> : items.length === 0 ? (
        <div className="bg-white rounded-2xl border border-sand-200 p-10 text-center text-muted-foreground flex flex-col items-center gap-3" data-testid="portal-events-empty">
          <div className="h-12 w-12 rounded-2xl bg-redline-50 flex items-center justify-center"><Sparkles className="h-6 w-6 text-redline-500" /></div>
          <div className="font-heading font-bold text-ink">{scope === "past" ? "Nothing on record yet" : "Nothing scheduled right now"}</div>
          <div className="text-sm">{scope === "past" ? "Past events will show here." : "Check back soon, or ask the concierge what's coming up."}</div>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((e) => (
            <div key={e.id} data-testid={`portal-event-${e.id}`} className="bg-white rounded-2xl border border-sand-200 p-4 sm:p-5">
              <div className="flex gap-3">
                {e.has_flyer && <img src={e.flyer_url} alt="" data-testid={`portal-event-flyer-${e.id}`} className="h-24 w-24 sm:h-28 sm:w-36 object-cover rounded-xl border border-sand-200 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${CATEGORY_COLORS[e.category] || CATEGORY_COLORS.other}`}>{e.category_label}</span>
                    <h3 className="font-heading font-bold text-ink text-base">{e.title}</h3>
                    {e.recurrence_text && <span className="inline-flex items-center gap-1 text-[11px] text-ink/60"><Repeat className="h-3 w-3" /> {e.recurrence_text}</span>}
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-ink/70">
                    <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5 text-ink/40" /> {e.date ? fmtDate(e.date) : "Date TBD"}{e.time ? ` · ${e.time}` : ""}{e.end_time ? `–${e.end_time}` : ""}</span>
                    {e.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-ink/40" /> {e.location}</span>}
                    {e.host_name && <span className="inline-flex items-center gap-1"><User className="h-3.5 w-3.5 text-ink/40" /> {e.host_name}{e.host_contact ? ` · ${e.host_contact}` : ""}</span>}
                  </div>
                  {e.description && <p className="text-sm text-ink/80 mt-1.5 whitespace-pre-wrap">{e.description}</p>}
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                    {e.rsvp_enabled && <span className="inline-flex items-center gap-1 text-ink/60"><Users className="h-3.5 w-3.5" /> {e.people} going{e.capacity ? ` · ${e.spots_left} of ${e.capacity} spots left` : ""}</span>}
                    {e.full && !e.my_rsvp && <span className="rounded-full bg-amber-50 text-amber-700 font-bold px-2 py-0.5">Full — waitlist open</span>}
                    {e.date && <a data-testid={`portal-event-ics-${e.id}`} href={e.ics_url} className="inline-flex items-center gap-1 rounded-full border border-sand-200 px-2.5 py-1 font-semibold text-ink/70 hover:border-ink"><CalendarPlus className="h-3.5 w-3.5" /> Add to my calendar</a>}
                  </div>
                  {e.rsvp_enabled && data.rsvp_enabled && !e.is_past && (
                    <div className="mt-3 flex flex-wrap items-center gap-2" data-testid={`portal-event-rsvp-${e.id}`}>
                      {e.my_rsvp ? (
                        <>
                          <span className={`inline-flex items-center gap-1.5 rounded-full text-xs font-bold px-3 py-1.5 ${e.my_waitlisted ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700"}`} data-testid={`portal-event-mine-${e.id}`}>
                            <Check className="h-3.5 w-3.5" /> {e.my_waitlisted ? `On the waitlist · party of ${e.my_rsvp}` : `You're going · party of ${e.my_rsvp}`}
                          </span>
                          <button data-testid={`portal-event-cancel-${e.id}`} disabled={busy === e.id} onClick={() => cancel(e)} className="inline-flex items-center gap-1 text-xs font-semibold text-ink/60 hover:text-rose-600"><X className="h-3.5 w-3.5" /> Can't make it</button>
                        </>
                      ) : null}
                      <label className="flex items-center gap-1.5 text-xs text-ink/70">Party of
                        <select data-testid={`portal-event-party-${e.id}`} value={party[e.id] || e.my_rsvp || 1} onChange={(ev) => setParty({ ...party, [e.id]: ev.target.value })} className="rounded-lg border border-sand-200 bg-white px-2 py-1 text-xs">
                          {[1, 2, 3, 4, 5, 6, 8, 10].map((n) => <option key={n} value={n}>{n}</option>)}
                        </select>
                      </label>
                      <button data-testid={`portal-event-rsvp-btn-${e.id}`} disabled={busy === e.id} onClick={() => rsvp(e)}
                        className="rounded-full bg-ink text-white text-xs font-bold px-4 py-1.5 hover:bg-ink-700 disabled:opacity-60">
                        {e.my_rsvp ? "Update" : e.full ? "Join waitlist" : "RSVP"}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
